import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Axios from 'axios';
import { Helmet } from 'react-helmet-async';

import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  RedditShareButton,
  TelegramShareButton,
  TwitterShareButton,
  WhatsappShareButton,
} from 'react-share';
import { ShareGlyph, ShareBubble } from './ShareIcons';

import { UserContext } from './userContext';
import { Loading } from './Loading';
import { BackDropLoad } from './LoadingBackDrop';
import { ContactForm } from './ContactFormDailog';
import { LoginForm } from './LoginFormDailog';
import { Recommended } from './Recommended';
import { PageContainer } from '../theme/primitives';

const key = 'dqweqwe123y97qhduiasd$@^!*#&bjasdja&^&@!#';

/* ─────────── Inline icons ─────────── */

const Icon = {
  Pin: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M12 22s-8-7.5-8-13a8 8 0 1 1 16 0c0 5.5-8 13-8 13z" />
      <circle cx="12" cy="9" r="3" />
    </svg>
  ),
  Tag: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M20.59 13.41 13 21 3 11V3h8z" />
      <circle cx="7.5" cy="7.5" r="1.2" />
    </svg>
  ),
  School: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
      <path d="M6 12v5c3 3 9 3 12 0v-5" />
    </svg>
  ),
  Timeline: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M3 12h4l3-9 4 18 3-9h4" />
    </svg>
  ),
  Star: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M12 2 15.09 8.26 22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  ),
  ArrowRight: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  ),
  Link: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  Check: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12l5 5L20 7" />
    </svg>
  ),
  Verified: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M9 12l2 2 4-4" />
      <path d="M12 2 4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5l-8-3z" />
    </svg>
  ),
};

/* ─────────── Detail card ─────────── */

function DetailCard({ icon, label, value }) {
  if (!value) return null;
  return (
    <div className="rounded-lg border border-border-subtle bg-surface-overlay/50 p-3.5 transition-[border-color,background-color] duration-200 ease-out-soft hover:border-border-strong hover:bg-surface-elevated/70">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-amber/24 bg-amber/10 text-amber shrink-0">
          {icon}
        </span>
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-fg-tertiary">{label}</span>
      </div>
      <div className="text-[13.5px] font-medium leading-snug text-fg-primary">{value}</div>
    </div>
  );
}

/* ═══════════════════ EngWf ═══════════════════ */

export const EngWf = () => {
  const { id } = useParams();
  const history = useNavigate();
  const url = window.location.href;
  const { user } = useContext(UserContext);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [recommended, setRecommended] = useState([]);

  const [wait, setWait] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    if (!id || isNaN(Number(id))) {
      history('/');
      return;
    }
    Axios.post('/new_apis/get-single-engwf', { id }, { headers: { key } }).then((res) => {
      if (res.data.status) {
        setData(res.data.data[0]);
        setRecommended(res.data.recommended || []);
        setLoading(false);
      } else {
        history('/');
      }
    });
  }, [id, history]);

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => { /* noop */ });
  };

  if (loading) return <Loading />;
  if (!data) return null;

  const initial = (data?.fname?.[0] || '?').toUpperCase();
  const fullName = `${data.fname || ''} ${data.lname || ''}`.trim();

  return (
    <>
      <Helmet>
        <title>{data.fname} - {data.subcat} | ESAY</title>
      </Helmet>

      <BackDropLoad wait={wait} />

      <PageContainer maxWidth="lg">

        {/* ═════ Hero ═════ */}
        <article className="relative isolate overflow-hidden rounded-2xl border border-border-subtle bg-surface-paper p-5 sm:p-6 md:p-7 mt-4 mb-5">
          {/* top accent line */}
          <div aria-hidden className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-amber/50 to-transparent" />
          {/* amber radial glow */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 -z-10 opacity-80"
            style={{ background: 'radial-gradient(800px 320px at 8% -10%, rgba(251,191,36,0.10), transparent 60%), radial-gradient(700px 320px at 100% 110%, rgba(251,191,36,0.06), transparent 60%)' }}
          />

          {/* Identity row */}
          <div className="flex flex-col sm:flex-row sm:items-start gap-5">
            {/* Avatar */}
            <div className="relative shrink-0 mx-auto sm:mx-0">
              {/* Outer pulse ring */}
              <span aria-hidden className="absolute -inset-1.5 rounded-2xl border border-amber/24 opacity-50" />
              <div
                className="
                  relative flex h-[112px] w-[112px] sm:h-[120px] sm:w-[120px]
                  items-center justify-center rounded-2xl
                  border border-amber/40
                  bg-gradient-to-br from-amber/30 to-amber/8
                  text-amber font-extrabold
                  text-[44px] sm:text-[48px] tracking-[-0.02em]
                  shadow-[0_8px_28px_-10px_rgba(251,191,36,0.45)]
                "
              >
                {initial}
              </div>
              {/* Verified badge */}
              <div
                aria-hidden
                title="Verified profile"
                className="absolute -bottom-1.5 -right-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-amber border-2 border-surface-paper shadow-[0_4px_10px_-2px_rgba(251,191,36,0.55)]"
              >
                <Icon.Check className="h-3.5 w-3.5 text-ink" />
              </div>
            </div>

            {/* Info */}
            <div className="flex min-w-0 flex-1 flex-col items-center sm:items-start text-center sm:text-left">
              {/* Category kicker */}
              <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-amber/24 bg-amber/10 px-2.5 py-1">
                <span aria-hidden className="inline-flex h-1.5 w-1.5 rounded-full bg-amber" style={{ boxShadow: '0 0 10px rgba(251,191,36,0.7)' }} />
                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber">{data.cat}</span>
              </div>

              {/* Name */}
              <h1 className="text-[22px] sm:text-[26px] md:text-[30px] font-bold leading-[1.15] tracking-[-0.025em] text-fg-primary">
                {fullName}
              </h1>

              {/* Amber gradient line */}
              <div aria-hidden className="mt-2.5 h-[2px] w-10 rounded-full bg-gradient-to-r from-amber to-amber/0" />

              {/* Inline meta */}
              <div className="mt-3 flex flex-wrap items-center justify-center sm:justify-start gap-x-2 gap-y-1 text-[13px] text-fg-secondary">
                {data.subcat && <span className="font-medium">{data.subcat}</span>}
                {data.area_ids && (
                  <>
                    <span aria-hidden className="text-fg-tertiary">·</span>
                    <span className="inline-flex items-center gap-1">
                      <Icon.Pin className="h-3 w-3 text-fg-tertiary" />
                      <span>{data.area_ids}</span>
                    </span>
                  </>
                )}
              </div>

              {/* CTA + Share */}
              <div className="mt-5 flex flex-col sm:flex-row items-center sm:items-center gap-3 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => {
                    if (user) setShowContactForm(true);
                    else setShowLoginForm(true);
                  }}
                  className="
                    group inline-flex h-11 w-full sm:w-auto items-center justify-center gap-1.5
                    rounded-full px-6
                    text-[12.5px] font-bold uppercase tracking-[0.1em]
                    bg-amber text-ink
                    hover:bg-amber-light hover:shadow-[0_0_0_3px_rgba(251,191,36,0.28),0_10px_28px_-8px_rgba(251,191,36,0.55)]
                    active:scale-[0.98]
                    transition-all duration-200 ease-out-soft
                  "
                >
                  Contact now
                  <Icon.ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                </button>

                {/* Compact share dock */}
                <div className="flex flex-wrap items-center gap-1 sm:ml-auto">
                  <FacebookShareButton url={url}><ShareBubble label="Facebook" size="sm"><ShareGlyph.Facebook /></ShareBubble></FacebookShareButton>
                  <LinkedinShareButton url={url}><ShareBubble label="LinkedIn" size="sm"><ShareGlyph.LinkedIn /></ShareBubble></LinkedinShareButton>
                  <TwitterShareButton url={url}><ShareBubble label="X" size="sm"><ShareGlyph.X /></ShareBubble></TwitterShareButton>
                  <RedditShareButton url={url}><ShareBubble label="Reddit" size="sm"><ShareGlyph.Reddit /></ShareBubble></RedditShareButton>
                  <WhatsappShareButton url={url}><ShareBubble label="WhatsApp" size="sm"><ShareGlyph.WhatsApp /></ShareBubble></WhatsappShareButton>
                  <TelegramShareButton url={url}><ShareBubble label="Telegram" size="sm"><ShareGlyph.Telegram /></ShareBubble></TelegramShareButton>
                  <EmailShareButton url={url}><ShareBubble label="Email" size="sm"><ShareGlyph.Email /></ShareBubble></EmailShareButton>
                  <button
                    type="button"
                    onClick={handleCopy}
                    aria-label={copied ? 'Link copied' : 'Copy link'}
                    title={copied ? 'Link copied!' : 'Click to copy the URL'}
                    className={`
                      inline-flex h-9 w-9 items-center justify-center rounded-full
                      border border-border-subtle bg-surface-elevated
                      transition-[background-color,border-color,color,transform] duration-200 ease-out-soft
                      hover:bg-amber hover:border-amber hover:text-ink hover:-translate-y-0.5
                      ${copied ? 'text-amber' : 'text-fg-secondary'}
                    `}
                  >
                    {copied ? <Icon.Check className="h-[15px] w-[15px]" /> : <Icon.Link className="h-[15px] w-[15px]" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* ═════ Profile details - grid of cards ═════ */}
        {(data.qualification || data.exp || data.certified_courses) && (
          <section className="mb-6">
            <div className="mb-3 flex items-center gap-3">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber">Profile</span>
              <span aria-hidden className="h-px flex-1 bg-gradient-to-r from-amber/50 to-transparent" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <DetailCard
                icon={<Icon.Tag className="h-3.5 w-3.5" />}
                label="Specialty"
                value={data.subcat ? `${data.cat} · ${data.subcat}` : data.cat}
              />
              <DetailCard
                icon={<Icon.Pin className="h-3.5 w-3.5" />}
                label="Service area"
                value={data.area_ids}
              />
              <DetailCard
                icon={<Icon.School className="h-3.5 w-3.5" />}
                label="Qualification"
                value={data.qualification}
              />
              <DetailCard
                icon={<Icon.Timeline className="h-3.5 w-3.5" />}
                label="Experience"
                value={data.exp}
              />
              <DetailCard
                icon={<Icon.Star className="h-3.5 w-3.5" />}
                label="Certifications"
                value={data.certified_courses}
              />
              <DetailCard
                icon={<Icon.Verified className="h-3.5 w-3.5" />}
                label="Status"
                value="Verified ESAY profile"
              />
            </div>
          </section>
        )}

        {recommended.length > 0 && <Recommended recs={recommended} />}

        {!user && (
          <LoginForm
            open={showLoginForm}
            showload={() => setWait(true)}
            hideload={() => setWait(false)}
            closeform={() => setShowLoginForm(false)}
            opencontactform={() => setShowContactForm(true)}
          />
        )}
        {user && (
          <ContactForm
            open={showContactForm}
            showload={() => setWait(true)}
            hideload={() => setWait(false)}
            closeform={() => setShowContactForm(false)}
            data={data}
            url="engwf"
          />
        )}
      </PageContainer>
    </>
  );
};
